import React from 'react'
import states from 'states-us'
import { isEmail, isPostalCode } from 'validator'
import * as yup from 'yup'

import { FormattedMessage } from '@divvy-web/i18n'

const poBoxPattern =
  /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i

export const isForeignAddress = yup.string().oneOf(['YES', 'NO']).default('NO').required()

export const street = yup
  .string()
  .max(99, () => (
    <FormattedMessage
      defaultMessage='Address is too long'
      id='sputnik.addressSchema__wIt2/i'
    />
  ))
  .test(
    'notEmail',
    () => (
      <FormattedMessage
        defaultMessage='Address cannot be an email'
        id='sputnik.addressSchema__iXe6th'
      />
    ),
    (value) => {
      if (!value) {
        return true
      }
      return !isEmail(value)
    },
  )

export const physicalStreet = street.matches(poBoxPattern, () => (
  <FormattedMessage
    defaultMessage='Address cannot be a PO Box'
    id='sputnik.addressSchema__/zWfSt'
  />
))

export const city = yup.string().max(39, () => (
  <FormattedMessage
    defaultMessage='City name is too long'
    id='sputnik.addressSchema__Q5+jZi'
  />
))

export const state = yup.string().typeError(() => (
  <FormattedMessage
    defaultMessage='Please enter a valid state'
    id='sputnik.addressSchema__PM4rA+'
  />
))

export const postalCode = yup.string().min(5, () => (
  <FormattedMessage
    defaultMessage='Postal code must be at least 5 digits'
    id='sputnik.addressSchema__hQ2cpM'
  />
))

export const countryCode = yup.string().default('US')

export const baseAddress = yup.object().shape({
  streetAdditional: street.nullable(),
  city: city.nullable(),
  state: state.nullable().when('countryCode', {
    is: 'US',
    then: (schema) =>
      schema.test(
        'isUsState',
        () => (
          <FormattedMessage
            defaultMessage='Please select a valid US state'
            id='sputnik.addressSchema__0m5Z5R'
          />
        ),
        (value) => {
          if (!value) {
            return true
          }
          return states.some(({ abbreviation }) => abbreviation === value)
        },
      ),
    otherwise: (schema) => schema,
  }),
  postalCode: postalCode.nullable().when('countryCode', ([countryCode], schema) =>
    schema.test(
      'validPostalCode',
      () => (
        <FormattedMessage
          defaultMessage='Please enter a valid postal code'
          id='sputnik.addressSchema__X0CRoi'
        />
      ),
      (value) => {
        if (!value) {
          return true
        }

        return isPostalCode(value, countryCode)
      },
    ),
  ),
  countryCode: countryCode.nullable(),
})

export const address = yup.object().concat(baseAddress).shape({
  street: street.nullable(),
})

export const physicalAddress = yup.object().concat(baseAddress).shape({
  street: physicalStreet.nullable(),
  streetAdditional: physicalStreet.nullable(),
})

export type PhysicalAddress = yup.InferType<typeof physicalAddress>
